/**
 * "Zimmer-Status" — how the rooms in operation are distributed (Slice 18).
 *
 * THE DENOMINATOR IS THE OPERATING POPULATION, never the inventory. The centre
 * number is `operating.totalRooms`, and the three slices are the counts from
 * the same occupancy rows that total was summed from. Mixing in inventory rooms
 * — the ones a not-yet-started unit owns — would draw a ring whose free segment
 * is made of rooms that cannot be let today.
 *
 * Because centre and slices come from the same rows, they agree. If a future
 * contract change ever made them disagree, the ring would visibly fail to close
 * rather than quietly renormalise itself — which is the honest failure.
 *
 * NO ZERO-PADDING FOR LOOKS. The reference shows four legend rows, two of them
 * at zero. Only states this page actually measures appear here: Belegt,
 * Reserviert and Frei. A fourth row pinned at 0 would be a claim about a state
 * nobody computes.
 *
 * The chart is `aria-hidden`; the legend beside it carries every count as text,
 * so nothing here is understandable only by colour.
 *
 * ZERO ROOMS SHOW NO SHARE (OC3). These three figures are the COMPOSITION of
 * the operating population, so each is a division by `total` -- and they obeyed
 * the same broken rule the rest of the admin surfaces did. The old `share()`
 * returned a plain 0 when there was nothing to divide, so a portfolio with no
 * rooms in operation read "Belegt 0 (0.0%)": a share of a population that does
 * not exist. The percent text now comes from the shared contract, which
 * declines to answer at a zero denominator. `0 of 4` is still "0.0%" -- four
 * rooms with nobody in them is a measurement, and it is not this case.
 *
 * THE GEOMETRY IS NOT THE TEXT, and only the text changed. Recharts lays the
 * arcs out from the raw counts (`dataKey="value"`), never from a percentage, so
 * no number was replaced by a string on the drawing path. At a zero denominator
 * every slice is 0, `drawable` is empty, and the placeholder ring below is
 * drawn instead of a chart. The centre figure stays `total`: zero rooms is
 * itself a count, and the counts were never the thing in doubt.
 */
import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import { occupancyPercentText } from "../../../utils/occupancyPercent";

/**
 * Slices, in the order the legend lists them. Zero-count categories keep their
 * legend row (the reader asked for "how many are reserved?" and 0 answers it)
 * but contribute no arc.
 *
 * Exported for tests.
 */
export function roomStatusSlices(operating) {
  if (!operating) return [];
  return [
    { key: "belegt", name: "Belegt", value: Number(operating.occupiedRooms) || 0, fill: "#10b981" },
    { key: "reserviert", name: "Reserviert", value: Number(operating.reservedRooms) || 0, fill: "#f59e0b" },
    { key: "frei", name: "Frei", value: Number(operating.freeRooms) || 0, fill: "#94a3b8" },
  ];
}

export default function CoLivingRoomStatusDonut({
  operating,
  unavailableNote,
}) {
  const slices = roomStatusSlices(operating);
  const total = operating ? Number(operating.totalRooms) || 0 : 0;
  const drawable = slices.filter((s) => s.value > 0);

  return (
    <section
      aria-label="Zimmer-Status"
      className="flex flex-col rounded-[10px] border border-border bg-card p-4"
    >
      <h2 className="m-0 text-[13px] font-semibold text-foreground">Zimmer-Status</h2>
      <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
        Verteilung der Zimmer im Betrieb
      </p>

      {!operating ? (
        <p className="m-0 mt-4 text-[12px] leading-[1.5] text-muted-foreground">
          {unavailableNote}
        </p>
      ) : (
        <div className="mt-3 flex flex-1 flex-wrap items-center gap-4">
          <div className="relative h-[132px] w-[132px] shrink-0" aria-hidden>
            {drawable.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={drawable}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={44}
                    outerRadius={62}
                    paddingAngle={drawable.length > 1 ? 2 : 0}
                    stroke="none"
                    isAnimationActive={false}
                  >
                    {drawable.map((s) => (
                      <Cell key={s.key} fill={s.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full w-full rounded-full border-[18px] border-muted" />
            )}
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-[20px] font-semibold leading-none text-foreground">
                {total}
              </span>
              <span className="mt-0.5 text-[10px] text-muted-foreground">Zimmer</span>
            </div>
          </div>

          <ul className="m-0 min-w-[124px] flex-1 list-none p-0">
            {slices.map((s) => (
              <li key={s.key} className="flex items-center gap-2 py-[5px]">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: s.fill }}
                  aria-hidden
                />
                <span className="flex-1 text-[12px] text-muted-foreground">{s.name}</span>
                <span className="font-mono text-[12px] font-medium text-foreground">
                  {`${s.value} (${occupancyPercentText({ total, occupied: s.value })})`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
